import { Alert, Linking, Platform } from 'react-native';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef } from './tipos';
import { dataBr, diasEntre, junta, num, op } from './util';
import { avisoRotinas, criarRotinas, lerHorarios, temRotinas, tirarRotinas } from './agenda';
import { today, horaTela } from '../lib/date';
import { consultaNoPlanner } from './agendaAuto';
import { N_, t } from '../lib/i18n';
import { acaoRelatorioPdf, dadosPdf, porDataPdf, secaoPdf } from '../lib/pdfFicha';
import type { BlocoPdf } from '../lib/pdf';
import { semBloqueio } from '../lib/ausencia';

const idade = (nasc?: string) => (nasc ? Math.floor(diasEntre(nasc, today()) / 365.25) : null);
export const textoIdade = (nasc?: string) => { const n = idade(nasc); return n == null ? '' : n < 1 ? t('menos de 1 ano') : t('{n} ano(s)', { n }); };

// ─── 0.70 (E1): o alarme do remédio toca mesmo no silencioso? (a pessoa responde em cada remédio novo) ───
// O valor guardado é o próprio texto da opção (como as outras opções das fichas).
export const SILENCIO_SEMPRE = N_('Tocar mesmo no silencioso e no Não perturbe');
export const SILENCIO_NORMAL = N_('Tocar só se o celular estiver no normal');
const MSG_IPHONE = N_('O modo silencioso do celular pode impedir o alarme. Deixe o celular no modo normal.');
/** Remédio toca mesmo no silencioso? Sem resposta (remédios de antes) = como era: só o alarme que insiste fura o silêncio. */
const tocaAlto = (r: Reg) => (r.c.silencioso ? r.c.silencioso !== SILENCIO_NORMAL : r.c.alarme !== false);

/** Abre a tela do Android "Acesso ao Não perturbe" (espera a volta, para não pedir a senha do app nem fechar o Diário). */
async function abrirAcessoNaoPerturbe() {
  const acao = 'android.settings.NOTIFICATION_POLICY_ACCESS_SETTINGS';
  try {
    const IL = require('expo-intent-launcher');
    await semBloqueio(() => IL.startActivityAsync(acao));
    return;
  } catch { /* app sem o módulo: abre pelo Linking */ }
  try { await semBloqueio(() => Linking.sendIntent(acao)); }
  catch { semBloqueio(() => Linking.openSettings()).catch(() => {}); }
}

/** Ao escolher "Tocar mesmo no silencioso": explica o que o celular precisa permitir. */
function explicarSilencio(v: string) {
  if (v !== SILENCIO_SEMPRE) return;
  if (Platform.OS === 'ios') return Alert.alert('🔕🔇', t(MSG_IPHONE));
  if (Platform.OS !== 'android') return;
  Alert.alert('🔕🔇 ' + t('Acesso ao Não perturbe'),
    t('Para o alarme do remédio tocar com o "Não perturbe" ligado, o celular precisa permitir o acesso ao "Não perturbe" para o My Planner. Toque em "Abrir configurações", procure o My Planner na lista e ligue a permissão.')
    + '\n\n' + t('Se o My Planner não aparecer na lista, essa permissão chega na próxima versão do app (atualize pela loja).'),
    [{ text: t('Agora não'), style: 'cancel' }, { text: t('Abrir configurações'), onPress: () => { abrirAcessoNaoPerturbe(); } }]);
}

/** Remédio: um lembrete por horário, todo dia (ou a cada X dias), com alarme que insiste até marcar como tomado. */
export function agendarRemedio(mod: 'saude' | 'familia', r: Reg, quem: string, calado = false) {
  const horas = lerHorarios(r.c.horarios);
  if (!horas.length) return calado ? undefined : Alert.alert(t('Informe os horários'), t('Ex.: 08:00, 14:00 e 20:00'));
  const n = criarRotinas(mod, r, horas.map(h => ({
    titulo: junta(r.c.nome, r.c.dose, quem) || t('Remédio'), emoji: '💊', hora: h, cada: num(r.c.cada) || 1,
    inicio: r.c.inicio || today(), fim: r.c.fim || null, insistente: r.c.alarme !== false, alto: tocaAlto(r), energia: 0,
    notas: r.c.notas || '',
  })));
  if (!calado) Alert.alert(t('Remédio na linha do tempo'), avisoRotinas(n) + (r.c.alarme !== false ? '\n' + t('O alarme toca de novo a cada 2 minutos (até 4 vezes) enquanto o remédio não for marcado como tomado.') : ''));
}

const pessoa: Colecao = {
  id: 'pessoa', nome: N_('Família'), um: N_('Pessoa'), icone: '🧑',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'nasc', r: N_('Data de nascimento'), t: 'data' },
    { k: 'sangue', r: N_('Tipo sanguíneo'), t: 'opcao', op: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] },
    { k: 'plano', r: N_('Plano de saúde'), t: 'texto' },
    { k: 'carteirinha', r: N_('Número da carteirinha'), t: 'cofre' },
    { k: 'alergias', r: N_('Alergias'), t: 'cofre' },
    { k: 'condicoes', r: N_('Doenças, cirurgias e observações'), t: 'cofre' },
    { k: 'emergencia', r: N_('Contato de emergência'), t: 'texto' },
    { k: 'telEmergencia', r: N_('Telefone de emergência'), t: 'telefone' },
    { k: 'hospital', r: N_('Hospital ou pronto-socorro de referência'), t: 'endereco' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(textoIdade(r.c.nasc), r.c.sangue, r.c.plano), ordem: 'titulo', anexos: true,
  acoes: (r, ctx) => [acaoRelatorioPdf(() => relatorioPessoa(ctx, r))],   // 0.65 (B15)
  vazio: N_('Cadastre você e cada pessoa da família. Dentro de cada uma ficam consultas, exames, vacinas e remédios. Dados sensíveis vão para o cofre criptografado.'),
};

const consulta: Colecao = {
  id: 'consulta', pai: 'pessoa', nome: N_('Consultas'), um: N_('Consulta'), icone: '🩺',
  campos: [
    { k: 'especialidade', r: N_('Especialidade'), t: 'texto', ph: N_('Ex.: Cardiologista') },
    { k: 'medico', r: N_('Médico ou clínica'), t: 'texto' },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [1, 0] },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'local', r: N_('Endereço'), t: 'endereco' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'resumo', r: N_('O que o médico disse'), t: 'cofre' },
    { k: 'retorno', r: N_('Retorno'), t: 'data', aviso: [7, 1] },
  ],
  titulo: r => junta(r.c.especialidade, r.c.medico), sub: r => junta(horaTela(r.c.hora), r.c.local, r.c.retorno && t('retorno {d}', { d: dataBr(r.c.retorno) })),
  data: 'data', anexos: true,
  agenda: (r, ctx) => ({ time: r.c.hora || null, title: junta(r.c.especialidade || t('Consulta'), ctx.porId(r.pai)?.c.nome), notes: junta(r.c.medico, r.c.local, r.c.telefone) }),
  // 0.65 (Texto 3 C): consulta com data vai sozinha para o My Planner
  aoSalvar: (r, ctx) => consultaNoPlanner('saude', r, { start: r.c.data, time: r.c.hora || null, title: junta(r.c.especialidade || t('Consulta'), ctx.porId(r.pai)?.c.nome), notes: junta(r.c.medico, r.c.local, r.c.telefone) }),
};

const exame: Colecao = {
  id: 'exame', pai: 'pessoa', nome: N_('Exames'), um: N_('Exame'), icone: '🧪',
  campos: [
    { k: 'nome', r: N_('Exame'), t: 'texto', ph: N_('Ex.: Hemograma') },
    { k: 'data', r: N_('Data'), t: 'data', alerta: true },
    { k: 'lab', r: N_('Laboratório'), t: 'texto' },
    { k: 'endLab', r: N_('Endereço do laboratório'), t: 'endereco' },
    { k: 'resultado', r: N_('Resultado'), t: 'cofre' },
  ],
  titulo: r => r.c.nome || '', sub: r => junta(dataBr(r.c.data), r.c.lab), ordem: 'data-desc', anexos: true,
  agenda: r => ({ start: r.c.data || today() }),
};

const vacina: Colecao = {
  id: 'vacina', pai: 'pessoa', nome: N_('Vacinas'), um: N_('Vacina'), icone: '💉',
  campos: [
    { k: 'nome', r: N_('Vacina'), t: 'texto' },
    { k: 'dose', r: N_('Dose'), t: 'opcao', op: [N_('1ª dose'), N_('2ª dose'), N_('3ª dose'), N_('Reforço'), N_('Dose única'), N_('Anual')] },
    { k: 'data', r: N_('Data da aplicação'), t: 'data' },
    { k: 'proxima', r: N_('Próxima dose'), t: 'data', aviso: [30, 7] },
    { k: 'lote', r: N_('Lote'), t: 'texto' },
    { k: 'local', r: N_('Local'), t: 'endereco' },
  ],
  titulo: r => junta(r.c.nome, op(r.c.dose)), sub: r => junta(r.c.data && t('aplicada em {d}', { d: dataBr(r.c.data) }), r.c.local),
  data: 'proxima', anexos: true,
  agenda: r => (r.c.proxima ? { start: r.c.proxima, title: t('Vacina: {nome}', { nome: r.c.nome }) } : null),
};

const remedio: Colecao = {
  id: 'remedio', pai: 'pessoa', nome: N_('Remédios'), um: N_('Remédio'), icone: '💊',
  campos: [
    { k: 'nome', r: N_('Remédio'), t: 'texto' },
    { k: 'dose', r: N_('Dose'), t: 'texto', ph: N_('Ex.: 1 comprimido') },
    { k: 'horarios', r: N_('Horários'), t: 'texto', ph: N_('Ex.: 08:00, 14:00 e 20:00') },
    { k: 'cada', r: N_('A cada quantos dias'), t: 'numero', ph: '1' },
    { k: 'inicio', r: N_('Início'), t: 'data' },
    { k: 'fim', r: N_('Fim (vazio = uso contínuo)'), t: 'data', alerta: true },
    { k: 'alarme', r: N_('Alarme que insiste até marcar como tomado'), t: 'sim' },
    { k: 'silencioso', r: N_('🔕🔇 O alarme deste remédio deve tocar mesmo com o celular no silencioso?'), t: 'opcao', op: [SILENCIO_SEMPRE, SILENCIO_NORMAL],
      obrigatorio: N_('Escolha se o alarme deste remédio deve tocar mesmo com o celular no silencioso.'), aoEscolher: explicarSilencio,
      dica: Platform.OS === 'ios' ? MSG_IPHONE : undefined },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => junta(r.c.nome, r.c.dose), sub: r => junta(lerHorarios(r.c.horarios).map(horaTela).join(' · '), num(r.c.cada) > 1 && t('a cada {n} dias', { n: num(r.c.cada) }), r.c.fim ? t('até {d}', { d: dataBr(r.c.fim) }) : t('contínuo'), temRotinas(r) && '⏰'),
  feito: r => !!r.c.fim && r.c.fim < today(),
  novo: () => ({ inicio: today(), alarme: true, cada: 1 }), anexos: true,
  // 0.65 (Texto 3 C): remédio com horários vai sozinho para o My Planner (é uma das exceções que entram automaticamente)
  aoSalvar: (r, ctx) => { if (lerHorarios(r.c.horarios).length && !(r.c.fim && r.c.fim < today())) agendarRemedio('saude', r, ctx.porId(r.pai)?.c.nome ?? '', true); },
  acoes: (r, ctx) => [
    { label: '⏰ ' + (temRotinas(r) ? t('Atualizar alarmes no My Planner') : t('Colocar na linha do tempo com alarme')), onPress: () => agendarRemedio('saude', r, ctx.porId(r.pai)?.c.nome ?? '') },
    ...(temRotinas(r) ? [{ label: t('Tirar alarmes do My Planner'), onPress: () => tirarRotinas('saude', r) }] : []),
  ],
  vazio: N_('Cadastre o remédio com os horários e toque em ⋮ para colocar na linha do tempo com alarme.'),
};

const historico: Colecao = {
  id: 'historico', pai: 'pessoa', nome: N_('Histórico'), um: N_('Registro clínico'), icone: '📋',
  campos: [
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Diagnóstico'), N_('Cirurgia'), N_('Internação'), N_('Alergia'), N_('Tratamento'), N_('Outro')] },
    { k: 'titulo', r: N_('Título'), t: 'texto' },
    { k: 'descricao', r: N_('Descrição'), t: 'cofre' },
  ],
  titulo: r => junta(op(r.c.tipo), r.c.titulo), sub: r => dataBr(r.c.data), ordem: 'data-desc', anexos: true,
};

/** 0.65 (B15): relatório de saúde da pessoa. Os campos do cofre (carteirinha, alergias, resultados...) não entram. */
function relatorioPessoa(ctx: Ctx, p: Reg) {
  const f = (col: string) => ctx.filhos(p.id, col);
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: junta(t('Saúde'), p.c.nome) },
    ...dadosPdf([
      [t('Data de nascimento'), junta(dataBr(p.c.nasc), textoIdade(p.c.nasc))], [t('Tipo sanguíneo'), p.c.sangue], [t('Plano de saúde'), p.c.plano],
      [t('Contato de emergência'), junta(p.c.emergencia, p.c.telEmergencia)], [t('Hospital ou pronto-socorro de referência'), p.c.hospital],
    ]),
    ...secaoPdf(t('Consultas'), [t('Data'), t('Horário'), t('Especialidade'), t('Médico ou clínica'), t('Retorno')],
      porDataPdf(f('consulta'), 'data', true).map(r => [dataBr(r.c.data), horaTela(r.c.hora), r.c.especialidade || '', r.c.medico || '', dataBr(r.c.retorno)]),
      [0.14, 0.1, 0.26, 0.36, 0.14]),
    ...secaoPdf(t('Exames'), [t('Data'), t('Exame'), t('Laboratório')],
      porDataPdf(f('exame'), 'data', true).map(r => [dataBr(r.c.data), r.c.nome || '', r.c.lab || '']), [0.16, 0.44, 0.4]),
    ...secaoPdf(t('Vacinas'), [t('Vacina'), t('Dose'), t('Data da aplicação'), t('Próxima dose'), t('Lote')],
      porDataPdf(f('vacina'), 'data', true).map(r => [r.c.nome || '', op(r.c.dose), dataBr(r.c.data), dataBr(r.c.proxima), r.c.lote || '']),
      [0.3, 0.16, 0.18, 0.18, 0.18]),
    ...secaoPdf(t('Remédios'), [t('Remédio'), t('Dose'), t('Horários'), t('Início'), t('Fim')],
      porDataPdf(f('remedio'), 'inicio', true).map(r => [r.c.nome || '', r.c.dose || '',
        junta(lerHorarios(r.c.horarios).map(horaTela).join(', '), num(r.c.cada) > 1 && t('a cada {n} dias', { n: num(r.c.cada) })), dataBr(r.c.inicio), r.c.fim ? dataBr(r.c.fim) : t('contínuo')]),
      [0.26, 0.18, 0.26, 0.15, 0.15]),
    ...secaoPdf(t('Histórico'), [t('Data'), t('Tipo'), t('Título')],
      porDataPdf(f('historico'), 'data', true).map(r => [dataBr(r.c.data), op(r.c.tipo), r.c.titulo || '']), [0.16, 0.24, 0.6]),
  ];
  return { nome: junta(t('Saúde'), p.c.nome), blocos: b };
}

export const SAUDE: ModDef = { id: 'saude', colecoes: [pessoa, consulta, exame, vacina, remedio, historico] };
